import React, { useState, useEffect } from 'react';
import axios from '../config/axios';
import '../styles/AdminDashboard.css';

function AdminDashboard() {
  const [users, setUsers] = useState([]);
  const [devices, setDevices] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editingUser, setEditingUser] = useState(null);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [activeTab, setActiveTab] = useState('users'); // users, devices, stats

  const token = localStorage.getItem('adminToken');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [usersRes, devicesRes, statsRes] = await Promise.all([
        axios.get('/admin/users', { headers: { Authorization: `Bearer ${token}` } }),
        axios.get('/admin/devices', { headers: { Authorization: `Bearer ${token}` } }),
        axios.get('/admin/stats', { headers: { Authorization: `Bearer ${token}` } })
      ]);
      
      setUsers(usersRes.data);
      setDevices(devicesRes.data);
      setStats(statsRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleEditUser = (user) => {
    setEditingUser({ ...user });
  };

  const handleSaveUser = async () => {
    try {
      await axios.put(`/admin/users/${editingUser._id}`, {
        name: editingUser.name,
        email: editingUser.email
      }, { headers: { Authorization: `Bearer ${token}` } });
      
      alert('User updated successfully');
      fetchData();
      setEditingUser(null);
    } catch (error) {
      alert('Failed to update user: ' + (error.response?.data?.error || 'Unknown error'));
    }
  };

  const handleResetPassword = async () => {
    if (newPassword.length < 6) {
      alert('Password must be at least 6 characters');
      return;
    }

    try {
      await axios.post(`/admin/users/${selectedUserId}/reset-password`, {
        newPassword
      }, { headers: { Authorization: `Bearer ${token}` } });
      
      alert('Password reset successfully');
      setShowPasswordModal(false);
      setNewPassword('');
      setSelectedUserId(null);
    } catch (error) {
      alert('Failed to reset password: ' + (error.response?.data?.error || 'Unknown error'));
    }
  };

  const handleDeleteUser = async (userId, userName) => {
    if (!window.confirm(`Are you sure you want to delete user: ${userName}?`)) return;

    try {
      await axios.delete(`/admin/users/${userId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      alert('User deleted successfully');
      fetchData();
    } catch (error) {
      alert('Failed to delete user: ' + (error.response?.data?.error || 'Unknown error'));
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('adminToken');
    localStorage.removeItem('adminUser');
    window.location.href = '/admin/login';
  };

  if (loading) {
    return <div className="loading">Loading...</div>;
  }

  return (
    <div className="admin-dashboard">
      <nav className="admin-nav">
        <h1>📊 Admin Dashboard</h1>
        <button onClick={handleLogout} className="logout-btn">Logout</button>
      </nav>

      <div className="admin-tabs">
        <button 
          className={activeTab === 'users' ? 'active' : ''} 
          onClick={() => setActiveTab('users')}
        >
          Users ({users.length})
        </button>
        <button 
          className={activeTab === 'devices' ? 'active' : ''} 
          onClick={() => setActiveTab('devices')}
        >
          Devices ({devices.length})
        </button>
        <button 
          className={activeTab === 'stats' ? 'active' : ''} 
          onClick={() => setActiveTab('stats')}
        >
          Statistics
        </button>
      </div>

      <div className="admin-content">
        {activeTab === 'users' && (
          <div className="users-section">
            <h2>User Management</h2>
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Auth Type</th>
                  <th>Created</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.map(user => (
                  <tr key={user._id}>
                    <td>{user.name}</td>
                    <td>{user.email}</td>
                    <td>{user.authType}</td>
                    <td>{new Date(user.createdAt).toLocaleDateString()}</td>
                    <td>
                      <button onClick={() => handleEditUser(user)} className="btn-edit">
                        ✏️ Edit
                      </button>
                      <button 
                        onClick={() => { setSelectedUserId(user._id); setShowPasswordModal(true); }} 
                        className="btn-password"
                      >
                        🔑 Reset Password
                      </button>
                      <button 
                        onClick={() => handleDeleteUser(user._id, user.name)} 
                        className="btn-delete"
                      >
                        🗑️ Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {editingUser && (
              <div className="modal-overlay" onClick={() => setEditingUser(null)}>
                <div className="modal" onClick={e => e.stopPropagation()}>
                  <h3>Edit User</h3>
                  <label>Name:</label>
                  <input
                    type="text"
                    value={editingUser.name}
                    onChange={e => setEditingUser({...editingUser, name: e.target.value})}
                  />
                  <label>Email:</label>
                  <input
                    type="email"
                    value={editingUser.email}
                    onChange={e => setEditingUser({...editingUser, email: e.target.value})}
                  />
                  <div className="modal-actions">
                    <button onClick={handleSaveUser} className="btn-save">Save</button>
                    <button onClick={() => setEditingUser(null)} className="btn-cancel">Cancel</button>
                  </div>
                </div>
              </div>
            )}

            {showPasswordModal && (
              <div className="modal-overlay" onClick={() => setShowPasswordModal(false)}>
                <div className="modal" onClick={e => e.stopPropagation()}>
                  <h3>Reset Password</h3>
                  <label>New Password:</label>
                  <input
                    type="password"
                    value={newPassword}
                    onChange={e => setNewPassword(e.target.value)}
                    placeholder="Enter new password"
                  />
                  <div className="modal-actions">
                    <button onClick={handleResetPassword} className="btn-save">Reset</button>
                    <button onClick={() => setShowPasswordModal(false)} className="btn-cancel">Cancel</button>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {activeTab === 'devices' && (
          <div className="devices-section">
            <h2>Device Management</h2>
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Device Name</th>
                  <th>Device ID</th>
                  <th>Type</th>
                  <th>Status</th>
                  <th>Owner</th>
                  <th>MQTT Topic</th>
                </tr>
              </thead>
              <tbody>
                {devices.map(device => (
                  <tr key={device._id}>
                    <td>{device.name}</td>
                    <td>{device.deviceId}</td>
                    <td>{device.type}</td>
                    <td className={`status ${device.status}`}>{device.status || 'unknown'}</td>
                    <td>{device.userId?.name || 'Unknown'}</td>
                    <td className="mqtt-topic">{device.mqttTopic}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {activeTab === 'stats' && stats && (
          <div className="stats-section">
            <h2>Statistics</h2>
            <div className="stats-grid">
              <div className="stat-card">
                <h3>📊 Total Users</h3>
                <p className="stat-value">{stats.totalUsers}</p>
              </div>
              <div className="stat-card">
                <h3>📱 Total Devices</h3>
                <p className="stat-value">{stats.totalDevices}</p>
              </div>
              <div className="stat-card">
                <h3>🟢 Online Devices</h3>
                <p className="stat-value">{stats.onlineDevices}</p>
              </div>
              <div className="stat-card">
                <h3>🔴 Offline Devices</h3>
                <p className="stat-value">{stats.offlineDevices}</p>
              </div>
            </div>

            <div className="devices-by-type">
              <h3>Devices by Type</h3>
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>Type</th>
                    <th>Count</th>
                  </tr>
                </thead>
                <tbody>
                  {stats.devicesByType.map(item => (
                    <tr key={item._id}>
                      <td>{item._id || 'other'}</td>
                      <td>{item.count}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="recent-users">
              <h3>Recent Users</h3>
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Email</th>
                    <th>Joined</th>
                  </tr>
                </thead>
                <tbody>
                  {stats.recentUsers.map(user => (
                    <tr key={user._id}>
                      <td>{user.name}</td>
                      <td>{user.email}</td>
                      <td>{new Date(user.createdAt).toLocaleDateString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default AdminDashboard;

